import React, { useCallback, useContext } from 'react';
import { Link as RouterLink, useParams } from 'react-router';
import { Box, Divider, Typography, Button, Stack, Link, Paper } from '@mui/material';

import config from '@shared/config';
import { FeedbackContext, LocalizationContext } from '@cvt/contexts';
import { useRouter } from '@cvt/routing';

import { Logo } from '@shared/components/Logo';

import { useSignup } from '../hooks/useSignup';
import { LoggedOutActionsLayout } from '../components/LoggedOutActionsLayout';
import { UserSignupForm } from '../partials/UserSignupForm';


export const SignUp = () => {
  const router = useRouter();
  // companySlug comes from the optional `/signup/:companySlug?` route segment
  const { companySlug } = useParams<{ companySlug?: string }>();
  const { genericErrorFeedback, triggerFeedback } = useContext(FeedbackContext);
  const { dictionary } = useContext(LocalizationContext);

  const { signupAndLogin, signupWithSSO } = useSignup();

  const onSSOLogin = useCallback(async (provider: string) => {
    try {
      await signupWithSSO(provider, { companySlug });
    } catch (e: any) {
      triggerFeedback({
        severity: 'error',
        message: e.message,
      });
    }
  }, [signupWithSSO, triggerFeedback, companySlug]);

  const onSubmit = useCallback(async (data: Auth.SignupWithEmailAndPassword) => {
    try {
      await signupAndLogin(data, companySlug);
      router.onboarding.go();
    } catch(e) {
      console.error(e);
      genericErrorFeedback();
    }
  }, [genericErrorFeedback, signupAndLogin, router, companySlug]);

  return (
    <LoggedOutActionsLayout>
      <Stack direction="row" alignItems="center" justifyContent="center" mb={3} position="relative">
        <Logo/>
      </Stack>
      <Paper component={Box} elevation={1} p={3}>
        {(config.featureFlags.appleSSO || config.featureFlags.googleSSO || config.featureFlags.microsoftSSO || config.featureFlags.phoneLogin) && (
          <>
            <Stack direction="column" justifyContent="center" spacing={2}>
              {config.featureFlags.googleSSO && (
                <Button
                  fullWidth
                  onClick={() => onSSOLogin('google')}
                  startIcon={<img src="/google.svg" alt="Google" width={18} height={18} />}
                  size="large"
                  variant="contained"
                  color="primary"
                >
                  {dictionary.auth.login.buttonLoginWithGoogle}
                </Button>
              )}
            </Stack>
            <Box my={3}>
              <Divider
                orientation="horizontal"
                flexItem
                sx={{
                  color: 'grey.500',
                  fontSize: 12,
                  fontWeight: 500,
                  textTransform: 'uppercase',
                }}
              >
                {dictionary.auth.divider}
              </Divider>
            </Box>
          </>
        )}
        <UserSignupForm
          onSubmitRequest={onSubmit}
          onSubmitButtonText={dictionary.auth.signUp.buttonSignup}
          hiddenFields={['phone']}
        />
      </Paper>
      <Box mt={3} textAlign="center">
        <Stack direction="row" justifyContent="center" alignItems="center" mt={1}>
          <Typography variant="body2" color="secondary.dark">
            {dictionary.auth.signUp.alreadyHaveAccount}
            {' '}
            <Link
              component={RouterLink}
              to={router.auth.login.path}
              underline="hover"
              color="secondary"
            >
              {dictionary.auth.signUp.buttonLogin}
            </Link>
          </Typography>
        </Stack>
      </Box>
    </LoggedOutActionsLayout>
  );
};
